import axios from "axios";
import API_URLS from "../../../config/global_variables";

const authToken = () => {
  try {
    return JSON.parse(localStorage.userData || "{}").token || "";
  } catch {
    return "";
  }
};

// TODO: bearer token — admin endpoints are currently open; once auth is added,
// rely on Authorization header below.
const headers = () => ({
  "Content-Type": "application/json",
  Authorization: authToken(),
});

const ERROR_MESSAGES = {
  VALIDATION_ERROR: "Please fix the highlighted fields.",
  NOT_FOUND: "Not found — it may have been deleted in another tab.",
  DUPLICATE_KEY: "Already exists — try a different value.",
  SERVER_ERROR: "Something went wrong. Please try again.",
};

const mapError = (err) => {
  const apiErr = err?.response?.data?.error;
  const code = apiErr?.code;
  const status = err?.response?.status;
  const fallback =
    apiErr?.message ||
    (status ? `Request failed (${status})` : err?.message || ERROR_MESSAGES.SERVER_ERROR);
  let message = ERROR_MESSAGES[code] || fallback;
  if (code === "VALIDATION_ERROR" && Array.isArray(apiErr?.details) && apiErr.details.length) {
    message = apiErr.details
      .map((d) => `${d.path || "field"}: ${d.message}`)
      .join("\n");
  }
  return {
    code: code || (status ? `HTTP_${status}` : "NETWORK_ERROR"),
    status,
    message,
    details: apiErr?.details || [],
  };
};

const buildListParams = (filters = {}) => {
  const params = {};
  if (filters.store_hash) params.store_hash = filters.store_hash;
  if (filters.store_label !== undefined && filters.store_label !== null && filters.store_label !== "")
    params.store_label = filters.store_label;
  if (filters.bamvideo_id) params.bamvideo_id = filters.bamvideo_id;
  if (filters.keyword) params.keyword = filters.keyword;
  if (filters.keyword_filter && filters.keyword_filter !== "all")
    params.keyword_filter = filters.keyword_filter;
  if (filters.min_points !== undefined && filters.min_points !== null && filters.min_points !== "")
    params.min_points = filters.min_points;
  if (filters.max_points !== undefined && filters.max_points !== null && filters.max_points !== "")
    params.max_points = filters.max_points;
  if (filters.is_default !== undefined && filters.is_default !== null && filters.is_default !== "")
    params.is_default = filters.is_default;
  if (filters.search) params.search = filters.search;
  if (filters.sort_by) params.sort_by = filters.sort_by;
  if (filters.sort_order) params.sort_order = filters.sort_order;
  params.page = filters.page || 1;
  params.limit = filters.limit || 20;
  return params;
};

const fetchVideos = async (filters) => {
  const res = await axios.get(API_URLS.BAMBUSER_VIDEOS, {
    headers: headers(),
    params: buildListParams(filters),
  });
  const payload = res?.data?.data || {};
  return {
    items: payload.items || [],
    pagination:
      payload.pagination || { page: 1, limit: filters.limit || 20, total: 0, total_pages: 0 },
  };
};

const fetchVideoById = async (id) => {
  const res = await axios.get(API_URLS.BAMBUSER_VIDEO_BY_ID(id), {
    headers: headers(),
  });
  return res?.data?.data || null;
};

const createVideo = async (payload) => {
  const res = await axios.post(API_URLS.BAMBUSER_VIDEOS, payload, {
    headers: headers(),
  });
  return res?.data?.data || null;
};

const updateVideo = async (id, patch) => {
  const res = await axios.put(API_URLS.BAMBUSER_VIDEO_BY_ID(id), patch, {
    headers: headers(),
  });
  return res?.data?.data || null;
};

const softDeleteVideo = async (id) => {
  const res = await axios.delete(API_URLS.BAMBUSER_VIDEO_BY_ID(id), {
    headers: headers(),
  });
  return res?.data?.data || null;
};

const hardDeleteVideo = async (id) => {
  const res = await axios.delete(API_URLS.BAMBUSER_VIDEO_HARD_DELETE(id), {
    headers: headers(),
  });
  return res?.data?.data || null;
};

const matchVideos = async (payload) => {
  const res = await axios.post(API_URLS.BAMBUSER_VIDEO_MATCH, payload, {
    headers: headers(),
  });
  return res?.data?.data || { items: [], fallback: false };
};

const addKeywordPoint = async (videoId, body) => {
  const res = await axios.post(API_URLS.BAMBUSER_VIDEO_KP(videoId), body, {
    headers: headers(),
  });
  return res?.data?.data || null;
};

const updateKeywordPoint = async (videoId, kpId, patch) => {
  const res = await axios.put(
    API_URLS.BAMBUSER_VIDEO_KP_BY_ID(videoId, kpId),
    patch,
    { headers: headers() }
  );
  return res?.data?.data || null;
};

const softDeleteKeywordPoint = async (videoId, kpId) => {
  const res = await axios.delete(API_URLS.BAMBUSER_VIDEO_KP_BY_ID(videoId, kpId), {
    headers: headers(),
  });
  return res?.data?.data || null;
};

// Bulk upsert: send all items in one call.
// items: [{ keyword, points, status? }]
// Returns { videoId, count, items[] }
const bulkUpsertKp = async (videoId, items) => {
  const res = await axios.post(
    API_URLS.BAMBUSER_VIDEO_KP_BULK(videoId),
    { items },
    { headers: headers() }
  );
  return res?.data?.data || { videoId, count: 0, items: [] };
};

const apiHelpers = {
  fetchVideos,
  fetchVideoById,
  createVideo,
  updateVideo,
  softDeleteVideo,
  hardDeleteVideo,
  matchVideos,
  addKeywordPoint,
  updateKeywordPoint,
  softDeleteKeywordPoint,
  bulkUpsertKp,
  mapError,
};

export default apiHelpers;
